/* eslint-disable */
import React, { useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Typography, IconButton, InputBase } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useNavigate } from 'react-router-dom';

import urbanTheme from 'views/urban-dashboard/theme/urbanTheme';
import { useUrbanMerchants } from 'hooks/useUrbanMerchants';
import { formatIndianCurrency, formatIndianCount } from 'views/urban-dashboard/utils/formatters';
import { exportToExcel } from 'views/urban-dashboard/utils/exportToExcel';
import PerPageSelector from 'views/urban-dashboard/components/PerPageSelector';

const STATUS_OPTIONS = ['ALL', 'ACTIVE', 'PENDING', 'REJECTED'];

/**
 * UrbanMerchantsList — Paginated, filterable merchants list page.
 * Matches the glassmorphic design language of the Urban Dashboard.
 */
function UrbanMerchantsList() {
    const navigate = useNavigate();
    const role = localStorage.getItem('role') === 'superdistributor' ? 'super-distributor' : 'distributor';
    const backPath = role === 'super-distributor' ? '/super-distributor/dashboard' : '/distributor/dashboard';

    const { state, data, totalCount, pagination, error, status, setStatus, search, setSearch, page, setPage, perPage, setPerPage, retry } = useUrbanMerchants({
        role
    });

    const [searchInput, setSearchInput] = useState('');

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    return (
        <ThemeProvider theme={urbanTheme}>
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(160deg, #F8FAFC 0%, #EEF2FF 35%, #F0FDF4 70%, #F8FAFC 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-120px',
                        right: '-80px',
                        width: '400px',
                        height: '400px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%)',
                        pointerEvents: 'none'
                    }
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 3, sm: 4, md: 5 }, px: { xs: 2, sm: 3.5, md: 5 } }}>
                    <main style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <BackButton onClick={() => navigate(backPath)} />
                            <Box>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{
                                        fontSize: { xs: '22px', sm: '28px' },
                                        fontWeight: 800,
                                        letterSpacing: '-0.03em',
                                        color: '#0F172A'
                                    }}
                                >
                                    Merchants List
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#64748B', mt: 0.25 }}>
                                    {totalCount > 0 ? `${totalCount} merchants` : 'Manage your merchant network'}
                                </Typography>
                            </Box>
                            {/* Export button */}
                            <ExportButton
                                onClick={() => {
                                    const endpoint =
                                        role === 'super-distributor'
                                            ? 'api/V2/moneytransfer/superDistributor/Urban/GetListMerchants'
                                            : 'api/V2/moneytransfer/distributor/Urban/GetListMerchants';
                                    const payload = { status };
                                    if (search) payload.search = search;
                                    exportToExcel(
                                        endpoint,
                                        payload,
                                        'merchants_list',
                                        [
                                            'sl_no',
                                            'merchant_name',
                                            'agent_code',
                                            'shop_name',
                                            'vpa',
                                            'branch_name',
                                            'status',
                                            'volume',
                                            'counts'
                                        ],
                                        {
                                            sl_no: '#',
                                            merchant_name: 'Merchant Name',
                                            agent_code: 'Agent Code',
                                            shop_name: 'Shop Name',
                                            vpa: 'VPA',
                                            branch_name: 'Branch',
                                            status: 'Status',
                                            volume: 'Volume',
                                            counts: 'Transactions'
                                        }
                                    );
                                }}
                                disabled={state !== 'loaded'}
                            />
                        </Box>

                        {/* Filters row */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3, alignItems: 'center' }}>
                            {/* Status tabs */}
                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                {STATUS_OPTIONS.map((opt) => (
                                    <FilterTab key={opt} label={opt} active={status === opt} onClick={() => setStatus(opt)} />
                                ))}
                            </Box>

                            {/* Search */}
                            <Box
                                component="form"
                                onSubmit={handleSearch}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '9px',
                                    border: '1px solid rgba(15, 23, 42, 0.08)',
                                    background: 'rgba(255, 255, 255, 0.7)',
                                    backdropFilter: 'blur(6px)',
                                    ml: 'auto',
                                    minWidth: { xs: '100%', sm: '220px' }
                                }}
                            >
                                <SearchIcon sx={{ fontSize: '16px', color: '#94A3B8' }} />
                                <InputBase
                                    placeholder="Search name, shop, code..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    sx={{ fontSize: '13px', flex: 1, '& input': { p: 0 } }}
                                />
                                {(searchInput || search) && (
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => {
                                            setSearchInput('');
                                            setSearch('');
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            border: 'none',
                                            background: 'rgba(148, 163, 184, 0.15)',
                                            cursor: 'pointer',
                                            p: 0,
                                            flexShrink: 0,
                                            transition: 'background 150ms ease',
                                            '&:hover': { background: 'rgba(148, 163, 184, 0.3)' }
                                        }}
                                        aria-label="Clear search"
                                    >
                                        <CloseIcon sx={{ fontSize: '13px', color: '#64748B' }} />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Content */}
                        {state === 'loading' && <LoadingSkeleton />}
                        {state === 'error' && <ErrorView message={error?.message} onRetry={retry} />}
                        {state === 'empty' && <EmptyView />}
                        {state === 'loaded' && (
                            <>
                                <MerchantTable data={data} from={pagination.from} />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 3 }}>
                                    <PerPageSelector value={perPage} onChange={setPerPage} />
                                    <Pagination
                                    page={page}
                                    lastPage={pagination.lastPage}
                                    currentPage={pagination.currentPage}
                                    setPage={setPage}
                                />
                                </Box>
                            </>
                        )}
                    </main>
                </Box>
            </Box>
        </ThemeProvider>
    );
}

/** Merchant table rows */
function MerchantTable({ data, from }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.map((m, i) => (
                <Box
                    key={m.agent_id || i}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr 1fr 1fr' },
                        gap: { xs: 1, sm: 2 },
                        px: { xs: 2, md: 3 },
                        py: { xs: 1.5, md: 1.75 },
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.02)',
                        transition: 'all 180ms ease',
                        alignItems: 'center',
                        '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 3px 12px -4px rgba(15, 23, 42, 0.05)',
                            borderColor: 'rgba(99, 102, 241, 0.12)'
                        }
                    }}
                >
                    {/* Name + shop */}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontSize: '13.5px',
                                fontWeight: 600,
                                color: '#1E293B',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {m.merchant_name || m.agent_name}
                        </Typography>
                        <Typography sx={{ fontSize: '11.5px', color: '#94A3B8', mt: 0.2 }}>
                            {m.shop_name} • {m.agent_code}
                        </Typography>
                    </Box>

                    {/* Branch */}
                    <Typography
                        sx={{
                            fontSize: '12.5px',
                            color: '#64748B',
                            display: { xs: 'none', sm: 'block' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {m.branch_name || m.Branch}
                    </Typography>

                    {/* Volume */}
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#059669',
                            fontFeatureSettings: '"tnum"',
                            display: { xs: 'none', sm: 'block' }
                        }}
                    >
                        {formatIndianCurrency(m.volume || 0)}
                    </Typography>

                    {/* Status badge */}
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <StatusBadge status={m.status} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

function StatusBadge({ status }) {
    const colors = {
        ACTIVE: { bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.15)', text: '#059669' },
        PENDING: { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.15)', text: '#D97706' },
        REJECTED: { bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.15)', text: '#DC2626' }
    };
    const c = colors[status] || colors.PENDING;
    return (
        <Box sx={{ display: 'inline-flex', px: 1.25, py: 0.35, borderRadius: '6px', background: c.bg, border: `1px solid ${c.border}` }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: c.text, letterSpacing: '0.02em' }}>{status}</Typography>
        </Box>
    );
}

function FilterTab({ label, active, onClick }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            sx={{
                px: 1.5,
                py: 0.6,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: active ? 'rgba(99, 102, 241, 0.4)' : 'rgba(15, 23, 42, 0.06)',
                background: active ? 'rgba(99, 102, 241, 0.06)' : 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(6px)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'all 180ms ease',
                '&:hover': { borderColor: 'rgba(99, 102, 241, 0.25)', background: 'rgba(255,255,255,0.85)' },
                '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
            }}
        >
            <Typography component="span" sx={{ fontSize: '12px', fontWeight: active ? 700 : 500, color: active ? '#4F46E5' : '#64748B' }}>
                {label}
            </Typography>
        </Box>
    );
}

function BackButton({ onClick }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '10px',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                background: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(8px)',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 200ms ease',
                '&:hover': { background: 'rgba(255,255,255,0.95)', borderColor: 'rgba(99,102,241,0.3)' },
                '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
            }}
        >
            <ArrowBackIcon sx={{ fontSize: '18px', color: '#475569' }} />
        </Box>
    );
}

function Pagination({ page, lastPage, currentPage, setPage }) {
    if (lastPage <= 1) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 3 }}>
            <IconButton
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    border: '1px solid rgba(15,23,42,0.08)',
                    background: 'rgba(255,255,255,0.7)'
                }}
            >
                <ChevronLeftIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                {currentPage} / {lastPage}
            </Typography>
            <IconButton
                disabled={page >= lastPage}
                onClick={() => setPage(page + 1)}
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    border: '1px solid rgba(15,23,42,0.08)',
                    background: 'rgba(255,255,255,0.7)'
                }}
            >
                <ChevronRightIcon sx={{ fontSize: '18px' }} />
            </IconButton>
        </Box>
    );
}

function LoadingSkeleton() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 8 }, (_, i) => (
                <Box
                    key={i}
                    sx={{
                        height: '56px',
                        borderRadius: '12px',
                        background: 'rgba(255,255,255,0.5)',
                        border: '1px solid rgba(255,255,255,0.7)',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${i * 0.06}s`,
                        '@keyframes pulse': { '0%,100%': { opacity: 0.5 }, '50%': { opacity: 1 } }
                    }}
                />
            ))}
        </Box>
    );
}

function ErrorView({ message, onRetry }) {
    return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: '15px', color: '#64748B', mb: 2 }}>{message || 'Something went wrong.'}</Typography>
            <Box
                component="button"
                onClick={onRetry}
                sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '10px',
                    border: '1px solid rgba(99,102,241,0.3)',
                    background: 'rgba(99,102,241,0.05)',
                    color: '#4F46E5',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    outline: 'none',
                    '&:hover': { background: 'rgba(99,102,241,0.1)' },
                    '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
                }}
            >
                Retry
            </Box>
        </Box>
    );
}

function EmptyView() {
    return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <StorefrontOutlinedIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 2 }} />
            <Typography sx={{ fontSize: '15px', color: '#64748B' }}>No merchants found.</Typography>
        </Box>
    );
}

function ExportButton({ onClick, disabled }) {
    return (
        <Box
            component="button"
            onClick={onClick}
            disabled={disabled}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.75,
                py: 0.85,
                ml: 'auto',
                borderRadius: '9px',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(8px)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'all 200ms ease',
                '&:hover:not(:disabled)': {
                    background: 'rgba(255,255,255,0.95)',
                    borderColor: 'rgba(99,102,241,0.3)',
                    boxShadow: '0 2px 8px -2px rgba(99,102,241,0.1)'
                },
                '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' }
            }}
        >
            <FileDownloadOutlinedIcon sx={{ fontSize: '16px', color: '#4F46E5' }} />
            <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap' }}>
                Export
            </Typography>
        </Box>
    );
}

export default UrbanMerchantsList;
